import { useInput, useInputButton, useInputIcon, wrappedInputPropsAdapter } from "../../input";
import { Box } from "../../box";
import { forwardRef } from "react";
import { ClearInputGroupContext, useInputGroupTextInputProps } from "../../input-group";
import { cssModule, isNil, mergeProps, omitProps, useChainedEventCallback, useControllableState } from "../../shared";
import { useResponsiveValue } from "../../styling";
import { useFieldInputProps } from "../../field";
import { useToolbarProps } from "../../toolbar";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DefaultElement = "input";
export function InnerTextInput(props) {
  const [toolbarProps] = useToolbarProps();
  const [fieldProps] = useFieldInputProps();
  const [inputGroupProps] = useInputGroupTextInputProps();
  const contextualProps = mergeProps({}, omitProps(toolbarProps, ["orientation"]), fieldProps, inputGroupProps);
  const {
    active,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    as = DefaultElement,
    autoFocus,
    button,
    defaultValue,
    disabled,
    fluid,
    focus,
    forwardedRef,
    hover,
    icon,
    id,
    loading,
    onChange,
    onValueChange,
    placeholder,
    readOnly,
    required,
    type = "text",
    validationState,
    value,
    wrapperProps: {
      as: wrapperAs = "div",
      ...userWrapperProps
    } = {},
    ...rest
  } = mergeProps(props, wrappedInputPropsAdapter(contextualProps));

  if (isNil(ariaLabel) && isNil(ariaLabelledBy) && isNil(placeholder)) {
    console.error("An input component must either have an \"aria-label\" attribute, an \"aria-labelledby\" attribute or a \"placeholder\" attribute.");
  }

  const fluidValue = useResponsiveValue(fluid);
  const [inputValue, setValue] = useControllableState(value, defaultValue, "");
  const handleChange = useChainedEventCallback(onChange, event => {
    const newValue = event.target.value;
    setValue(newValue);

    if (!isNil(onValueChange)) {
      onValueChange(event, newValue);
    }
  });
  const {
    inputProps,
    wrapperProps
  } = useInput({
    active,
    autoFocus,
    cssModule: "o-ui-text-input",
    disabled,
    fluid: fluidValue,
    focus,
    forwardedRef,
    hover,
    id,
    loading,
    onChange: handleChange,
    placeholder,
    readOnly,
    required,
    type,
    validationState,
    value: inputValue
  });
  const iconMarkup = useInputIcon(icon, {
    disabled
  });
  const buttonMarkup = useInputButton(button, !disabled && !readOnly);

  const content = /*#__PURE__*/_jsxs(_Fragment, {
    children: [iconMarkup, /*#__PURE__*/_jsx(Box, { ...mergeProps(rest, {
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledBy,
        as
      }, inputProps)
    }), /*#__PURE__*/_jsx(ClearInputGroupContext, {
      children: buttonMarkup
    })]
  });

  return /*#__PURE__*/_jsx(Box, { ...mergeProps(userWrapperProps, {
      as: wrapperAs,
      className: cssModule("o-ui-input", iconMarkup && "has-icon", disabled && "disabled", buttonMarkup && "has-button")
    }, wrapperProps),
    children: content
  });
}
InnerTextInput.defaultElement = DefaultElement;
export const TextInput = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerTextInput, { ...props,
  forwardedRef: ref
}));